'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, MessageCircle, Send, Calendar, Bell } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NAV_ITEMS = [
  { id: 'nav-home', label: 'Accueil', icon: Home, href: '/' },
  { id: 'nav-messages', label: 'Messages', icon: MessageCircle, href: '/messages' },
  { id: 'nav-send', label: 'Envoyer', icon: Send, href: '/send-screen' },
  { id: 'nav-agenda', label: 'Agenda', icon: Calendar, href: '/agenda' },
  { id: 'nav-alerts', label: 'Alertes', icon: Bell, href: '/alerts-screen' },
];

interface BottomNavProps {
  alertCount?: number;
  messageCount?: number;
}

export default function BottomNav({ alertCount = 0, messageCount = 0 }: BottomNavProps) {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    // activities is a sub-page, don't highlight any nav item for it
    if (pathname.startsWith('/activities')) return false;
    return pathname.startsWith(href);
  };

  return (
    <nav
      className="safe-bottom sticky bottom-0 z-40 bg-card border-t border-border shadow-nav"
      aria-label="Navigation principale"
    >
      <div className="flex items-stretch">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.href);
          const Icon = item.icon;
          const badge = item.id === 'nav-alerts' ? alertCount : item.id === 'nav-messages' ? messageCount : 0;

          return (
            <Link
              key={item.id}
              href={item.href}
              className={`
                flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 relative
                tap-target transition-all duration-150
                ${active
                  ? 'text-lucat-navy' :'text-muted-foreground hover:text-lucat-navy/70'
                }
              `}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-lucat-green rounded-b-full" />
              )}

              <div className="relative">
                <Icon
                  size={22}
                  strokeWidth={active ? 2.5 : 1.8}
                  className={active ? 'text-lucat-navy' : ''}
                />
                {badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-lucat-red text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </div>

              <span className={`text-[10px] font-semibold tracking-tight ${active ? 'text-lucat-navy' : ''}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}